// src/oura/ui/Drawer.tsx
//
// The right-hand sheet every new drawer opens in: the question, a fix, the
// answers, a month, a report. The shell only; what goes inside belongs to
// each screen. The older ActionDrawer and IssueFixDrawer keep their own
// widths and flows.
//
// API (drawer specs v1.1, 29 Sep 2026). Everything below is opt in: a drawer
// that passes `title`, `sub` and `foot` renders exactly as it did before.
//
//   One shell per host, one keyed sheet inside it:
//     <Drawer open onClose nav? back? itemKey? followKeyboard? returnFocus? width?>
//       <DocSheet key={doc.id} ... />
//     </Drawer>
//   Leave `title` out and the sheet draws its own header and foot, which
//   portal into the shell (drawerSlots.ts):
//     <DrawerHead lead? leadKind? title status? chips? clampTitle? />
//     <DrawerFoot message? first? second? confirm? done? />
//
//   Drawer, new props
//     nav        DrawerNav: Row A shows "2 of 3", previous, next and X. J/K
//                when `nav.keys` (staff only). Hidden for 0 or 1 items.
//     back       DrawerBack: "‹ {label}" in Row A for a sub-view; hides nav.
//                Escape calls back.onBack before it closes the drawer.
//     itemKey    changes when the sheet shows another item (or a sub-view):
//                the body scrolls to the top, focus moves (below), and a
//                polite region says "{title}. {status}. 2 of 3."
//     title      now optional; leave it out when the sheet renders DrawerHead.
//     followKeyboard  under 640px, the sheet follows the visual viewport while
//                a text field inside it has focus, so the foot stays above
//                the phone keyboard. Only after the A12 device test passes.
//     The 44px close target is on for every drawer using the new header;
//     `closeTapTarget` still opts an old one in.
//
//   Focus on open and on a move: the title, or the field the sheet claimed
//   with useDrawerFocusClaim() (drawerHooks.ts). A move from the arrows keeps
//   focus on the arrow while it is still enabled. On close: returnFocus(),
//   then whatever opened the drawer (returnFocusChain builds the lookup).
//
//   DrawerHead   lead (IconSquare with leadKind="square", or a 14px icon),
//                the h2 (wraps; clampTitle cuts at 2 lines, full text on
//                hover), chips (identity only), then the status line
//                "● {label} · {clock}", the dialog's description.
//   DrawerFoot   at most two pills: `first` (primary only when it is the
//                viewer's move) and `second`. `message` is a line above them:
//                success and info are said aloud, error is role=alert.
//                `confirm` swaps the pills for a sentence, [verb] [Cancel];
//                Cancel takes focus and Escape cancels. `done` swaps them for
//                "✓ {said}. 2 left in Needs us." and one blue Next or Done
//                button that takes focus. Renders nothing, and the foot
//                hides, when given nothing.
//   DrawerBlock  a white block with a label; `busy` sets aria-busy while it is written.
//   DrawerFold   a block whose head folds it, folded by default; `note` shows
//                one fact while folded. `remember` (staff) keeps the state
//                under oura.fold.drawer.<id>; merchant folds do not remember.
//   DrawerQuote  the other side's words: "who · where · date", then the text
//                cut at 6 lines with "Show all".
//   DrawerNotice the amber notice at the top of the body, for a failed or stale read.
//   DrawerPulse  loading bars with an sr-only "Loading…", inside a block.

import {
    useCallback,
    useContext,
    useEffect,
    useId,
    useLayoutEffect,
    useMemo,
    useRef,
    useState,
    type KeyboardEvent,
    type MouseEvent,
    type ReactNode,
} from "react";
import { createPortal } from "react-dom";
import { CheckIcon, ChevronDownIcon, ChevronLeftIcon, ChevronUpIcon, TriangleAlertIcon, XIcon } from "lucide-react";
import { cn } from "../lib/cn";
import { dayDate, joinDotted } from "../lib/oura";
import { useDrawerChrome } from "../lib/useDrawerChrome";
import { Dot } from "./Chips";
import { PillButton } from "./Controls";
import { DrawerSlotContext, type DrawerBack, type DrawerNav, type DrawerSeqItem, type DrawerSlotApi, type DrawerStatus } from "./drawerSlots";
import { useEscapeLayer } from "./escapeLayer";
import { foldKey, useFoldState } from "./foldState";
import { FOCUS_RING, LATE_TEXT, PAGE_GROUND, SURFACE, TYPE } from "./tokens";
import { useVisualViewport } from "./useVisualViewport";
import { useClientShell } from "./Fold";

const FOCUSABLE = 'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])';

/** Where J and K type rather than move. */
const TYPING = 'input, textarea, select, [contenteditable]:not([contenteditable="false"]), [role="radio"], [role="textbox"], [role="combobox"], [role="listbox"], [role="menu"]';

/**
 * The controls Tab can actually reach. The selector alone also matches a
 * display:none file input behind an "Attach" label, and a trap whose last
 * stop is invisible never sees focus arrive there, so Tab walked out of the
 * sheet into the page behind it.
 */
function tabStops(root: HTMLElement): HTMLElement[] {
    return Array.from(root.querySelectorAll<HTMLElement>(FOCUSABLE)).filter(
        (n) => n.getClientRects().length > 0 && !n.closest("[hidden],[inert]"),
    );
}

/** Cut to `n` characters with an ellipsis; the whole text goes in an accessible name or tooltip. */
function cut(text: string, n: number): string {
    return text.length > n ? `${text.slice(0, n - 1).trimEnd()}…` : text;
}

/** Parts read aloud as sentences, without doubling a question mark into "?.". */
function sentences(parts: Array<string | null | undefined>): string {
    return parts
        .map((p) => (p ?? "").replace(/\s+/g, " ").trim())
        .filter(Boolean)
        .map((p) => (/[.?!…]$/.test(p) ? p : `${p}.`))
        .join(" ");
}

/** The 30px round header button. */
const ROUND =
    "grid h-[30px] w-[30px] shrink-0 place-items-center rounded-full bg-white text-gray-900 ring-1 ring-inset ring-black/[0.08] hover:bg-gray-50";
/** 30 + 2 x 7 = 44px; the header's padding has room for it. */
const ROUND_TAP = "relative after:absolute after:-inset-[7px] after:content-['']";
/** In Row A the three buttons sit 8px apart: 44px tall and 38px wide, so no target covers its neighbour. */
const ROUND_TAP_ROW = "relative after:absolute after:-inset-x-1 after:-inset-y-[7px] after:content-['']";
const ROUND_OFF = "disabled:cursor-not-allowed disabled:opacity-40 disabled:hover:bg-white";

/** Where focus goes when a drawer closes; see `DrawerProps.returnFocus`. */
export type DrawerReturnFocus = () => HTMLElement | null;

export interface DrawerProps {
    open: boolean;
    onClose: () => void;
    /** The old header. Leave it out when the sheet renders <DrawerHead>. */
    title?: ReactNode;
    sub?: ReactNode;
    /** Pills along the bottom (the old foot). Leave it out when the sheet renders <DrawerFoot>. */
    foot?: ReactNode;
    /** Pixels; never wider than the viewport. */
    width?: number;
    /**
     * Where focus lands on close, looked up a frame after closing so a list
     * the close changed has re-rendered: a row that moved to another group,
     * or a table row opened by pointer (not a control, so it never held
     * focus). Falls back to whatever had focus when the drawer opened.
     */
    returnFocus?: DrawerReturnFocus;
    /**
     * Opt in: the `sub` line is the dialog's description (aria-describedby),
     * so a screen reader reads it on open, for a sub that says something the
     * title does not (who a request goes to).
     */
    describeWithSub?: boolean;
    /** Opt in: a 44px pseudo-element target round the 30px close button, for a phone. It looks the same. On for every drawer using the new header. */
    closeTapTarget?: boolean;
    /** Opt in: previous and next through the group the drawer opened from. */
    nav?: DrawerNav;
    /** Opt in: a sub-view, with "‹ {label}" in place of the counter. */
    back?: DrawerBack;
    /** Opt in: which item (or sub-view) the sheet shows; a change is a move. */
    itemKey?: string;
    /** Opt in: keep the foot above a phone keyboard. Only once it passes the device test (A12). */
    followKeyboard?: boolean;
    children: ReactNode;
}

/**
 * One drawer at a time: the caller holds which one is open, and opening
 * another replaces the content rather than stacking a second sheet. Give the
 * content a `key` per item (and pass `itemKey`) when moving to another item.
 *
 * Escape closes it through the shared escape stack, so a lightbox opened
 * inside it (with useEscapeLayer) closes first, and a sub-view goes back
 * before the drawer closes. Focus moves to the title (or a claimed field) on
 * open, stays inside while open, and on close goes to `returnFocus()` or,
 * failing that, back to whatever opened it.
 */
export function Drawer({
    open,
    onClose,
    title,
    sub,
    foot,
    width = 600,
    returnFocus,
    describeWithSub,
    closeTapTarget,
    nav,
    back,
    itemKey,
    followKeyboard,
    children,
}: DrawerProps) {
    const titleId = useId();
    const subId = useId();
    const statusId = useId();
    /** The old header's h2. */
    const titleRef = useRef<HTMLHeadingElement>(null);
    /** DrawerHead's h2, portalled in by the sheet. */
    const headTitleRef = useRef<HTMLElement | null>(null);
    const statusRef = useRef<HTMLElement | null>(null);
    /** The field a sheet claimed focus for (useDrawerFocusClaim). */
    const claimRef = useRef<HTMLElement | null>(null);
    const sheetRef = useRef<HTMLElement>(null);
    const bodyRef = useRef<HTMLDivElement>(null);
    const rowARef = useRef<HTMLDivElement>(null);
    const liveRef = useRef<HTMLDivElement>(null);
    /** Focus still owed to the title or the claimed field: set on open and on a move. */
    const pendingFocus = useRef(false);
    /** How the last move was made from this shell's own controls. */
    const lastVia = useRef<"button" | "key" | null>(null);
    /**
     * A J/K move owes focus to the title, never to a field the new sheet
     * claims: in a field, the next J would type "j" into the draft instead
     * of moving on.
     */
    const skipClaim = useRef(false);
    const [targets, setTargets] = useState<{ head: HTMLElement | null; foot: HTMLElement | null }>({ head: null, foot: null });
    const [hasStatus, setHasStatus] = useState(false);

    // Read at close time, so the caller's latest lookup is the one that runs.
    const returnFocusRef = useRef(returnFocus);
    useEffect(() => {
        returnFocusRef.current = returnFocus;
    }, [returnFocus]);
    // Read a frame after a move, for the announcement.
    const navRef = useRef(nav);
    useLayoutEffect(() => {
        navRef.current = nav;
    });

    // A sub-view's Escape goes back to the view under it; otherwise it closes.
    useDrawerChrome(open, back ? back.onBack : onClose);

    const keyboardStyle = useVisualViewport(sheetRef, open && Boolean(followKeyboard));

    /** The control that had focus when the drawer opened, if any did. */
    const openerRef = useRef<HTMLElement | null>(null);

    const tryFocus = useCallback(() => {
        if (!pendingFocus.current) return;
        const claim = skipClaim.current ? null : claimRef.current;
        const target = claim?.isConnected ? claim : titleRef.current ?? headTitleRef.current;
        if (!target?.isConnected) return;
        pendingFocus.current = false;
        skipClaim.current = false;
        target.focus();
    }, []);

    const announce = useCallback((text: string) => {
        const el = liveRef.current;
        if (!el || !text) return;
        // Cleared first, so the same words said twice are said twice.
        el.textContent = "";
        requestAnimationFrame(() => {
            if (el.isConnected) el.textContent = text;
        });
    }, []);

    // Callback refs, and stable ones: comparing first keeps a repeat call
    // from re-setting the same node (see PageSlots).
    const setHead = useCallback((el: HTMLElement | null) => setTargets((t) => (t.head === el ? t : { ...t, head: el })), []);
    const setFoot = useCallback((el: HTMLElement | null) => setTargets((t) => (t.foot === el ? t : { ...t, foot: el })), []);
    const setTitleEl = useCallback(
        (el: HTMLElement | null) => {
            headTitleRef.current = el;
            // A head a sheet renders late (after its own load) still gets the
            // focus it is owed, once every ref of that commit is attached.
            if (el) queueMicrotask(tryFocus);
        },
        [tryFocus],
    );
    const setStatusEl = useCallback((el: HTMLElement | null) => {
        statusRef.current = el;
        setHasStatus(Boolean(el));
    }, []);
    const claimFocus = useCallback(
        (el: HTMLElement) => {
            claimRef.current = el;
            queueMicrotask(tryFocus);
            return () => {
                if (claimRef.current === el) claimRef.current = null;
            };
        },
        [tryFocus],
    );

    const navKeys = Boolean(nav?.keys) && !back;
    const slots = useMemo<DrawerSlotApi>(
        () => ({ head: targets.head, foot: targets.foot, titleId, statusId, navKeys, setTitleEl, setStatusEl, claimFocus, announce }),
        [targets.head, targets.foot, titleId, statusId, navKeys, setTitleEl, setStatusEl, claimFocus, announce],
    );

    // Layout effect: the opener must be read before focus leaves it.
    useLayoutEffect(() => {
        if (!open) return;
        const active = document.activeElement;
        // Only a control outside the sheet: re-running this effect while open
        // would otherwise record the sheet's own title as the opener.
        if (active instanceof HTMLElement && active !== document.body && !sheetRef.current?.contains(active)) {
            openerRef.current = active;
        }
        pendingFocus.current = true;
        skipClaim.current = false;
        return () => {
            pendingFocus.current = false;
            // A frame later: a close that moves the opened row (under Later,
            // into Done) re-renders the list first, and the target has to be
            // looked up in the new one.
            requestAnimationFrame(() => {
                // Something took focus in the meantime (the next drawer's
                // title, a dialog the close opened): leave it there.
                const now = document.activeElement;
                if (now && now !== document.body) return;
                const target = returnFocusRef.current?.();
                const opener = openerRef.current;
                openerRef.current = null;
                if (target?.isConnected) target.focus();
                else if (opener?.isConnected) opener.focus();
            });
        };
    }, [open]);

    // A move: another item (or sub-view) in the same shell.
    const seenKey = useRef<{ key: string | undefined } | null>(null);
    useLayoutEffect(() => {
        if (!open) {
            seenKey.current = null;
            return;
        }
        if (!seenKey.current) {
            seenKey.current = { key: itemKey };
            return;
        }
        if (seenKey.current.key === itemKey) return;
        seenKey.current.key = itemKey;
        if (bodyRef.current) bodyRef.current.scrollTop = 0;
        const via = lastVia.current;
        lastVia.current = null;
        // An arrow keeps focus while it can still be pressed, so it can be
        // pressed again; J/K, Next and a sub-view send it to the new title.
        const active = document.activeElement;
        const keep = via === "button" && active instanceof HTMLButtonElement && !active.disabled && Boolean(rowARef.current?.contains(active));
        pendingFocus.current = !keep;
        skipClaim.current = via === "key";
        requestAnimationFrame(() => {
            const n = navRef.current;
            announce(
                sentences([
                    (titleRef.current ?? headTitleRef.current)?.textContent,
                    statusRef.current?.textContent,
                    n && n.total > 1 && n.index >= 0 && !n.settled ? `${n.index + 1} of ${n.total}` : null,
                ]),
            );
        });
    }, [open, itemKey, announce]);

    // Every commit: pay any focus still owed, now that this commit's refs
    // (the portalled title, a claimed field) are all attached.
    useLayoutEffect(() => {
        if (open) tryFocus();
    });
    const clientShell = useClientShell();

    if (!open) return null;

    const hasNav = Boolean(nav && nav.total > 1 && nav.index >= 0);
    const rowA = Boolean(back) || hasNav;
    const modern = title === undefined || Boolean(nav) || Boolean(back);
    const whiteBody = clientShell;
    const tap = closeTapTarget || modern;
    const describedBy = hasStatus ? statusId : describeWithSub && sub ? subId : undefined;

    const move = (to: DrawerSeqItem | null, via: "button" | "key") => {
        if (!nav || !to || nav.disabled) return;
        lastVia.current = via;
        nav.onMove(to.id, via);
    };

    // aria-modal promises the page behind is out of reach, so Tab wraps
    // inside the sheet instead of walking off into it.
    const trapTab = (e: KeyboardEvent<HTMLElement>) => {
        if (e.key !== "Tab" || !sheetRef.current) return;
        const nodes = tabStops(sheetRef.current);
        if (nodes.length === 0) return;
        const first = nodes[0];
        const last = nodes[nodes.length - 1];
        const active = document.activeElement;
        const heading = titleRef.current ?? headTitleRef.current;
        if (e.shiftKey && (active === first || active === heading)) {
            e.preventDefault();
            last.focus();
        } else if (!e.shiftKey && active === last) {
            e.preventDefault();
            first.focus();
        }
    };

    const onKeyDown = (e: KeyboardEvent<HTMLElement>) => {
        trapTab(e);
        if (e.defaultPrevented || !nav || !navKeys || nav.disabled) return;
        if (e.altKey || e.ctrlKey || e.metaKey || e.shiftKey || e.nativeEvent.isComposing) return;
        const key = e.key.toLowerCase();
        if (key !== "j" && key !== "k") return;
        if (e.target instanceof Element && e.target.closest(TYPING)) return;
        const to = key === "j" ? nav.next : nav.prev;
        if (!to) return;
        e.preventDefault();
        move(to, "key");
    };

    const closeButton = (inRow: boolean) => (
        <button type="button" onClick={onClose} aria-label="Close" className={cn(ROUND, inRow ? ROUND_TAP_ROW : tap && ROUND_TAP, FOCUS_RING)}>
            <XIcon aria-hidden size={16} />
        </button>
    );

    const oldTitle = (
        <>
            <h2 ref={titleRef} id={titleId} tabIndex={-1} className={cn(TYPE.tt, "m-0 text-gray-900 outline-none")}>
                {title}
            </h2>
            {sub && (
                <div id={describeWithSub ? subId : undefined} className={cn(TYPE.tc, "mt-1 text-gray-500")}>
                    {sub}
                </div>
            )}
        </>
    );

    const header = !modern ? (
        <div className="flex items-start justify-between gap-3 border-b border-gray-100 px-6 py-5">
            <div className="min-w-0">{oldTitle}</div>
            {closeButton(false)}
        </div>
    ) : (
        <>
            {rowA && (
                <div ref={rowARef} className="flex min-h-11 items-center gap-2 px-6 pt-3 max-sm:px-4">
                    {back ? (
                        <button
                            type="button"
                            onClick={back.onBack}
                            aria-label={`Back to ${back.label}`}
                            title={back.label.length > 30 ? back.label : undefined}
                            className={cn(
                                TYPE.tc,
                                "relative -ml-1 inline-flex min-w-0 items-center gap-0.5 rounded-md px-1 font-medium text-blue-600 hover:text-blue-700",
                                "after:absolute after:inset-x-0 after:-inset-y-[13px] after:content-['']",
                                FOCUS_RING,
                            )}
                        >
                            <ChevronLeftIcon aria-hidden size={14} className="shrink-0" />
                            <span className="truncate">{cut(back.label, 30)}</span>
                        </button>
                    ) : (
                        // Once the item has left its group (a completing
                        // action), "3 of 3" would count it; the foot says what is left.
                        nav &&
                        !nav.settled && (
                            <span aria-hidden className={cn(TYPE.tc, "tabular-nums text-gray-500")}>
                                {nav.index + 1} of {nav.total}
                            </span>
                        )
                    )}
                    <div className="ml-auto flex shrink-0 items-center gap-2">
                        {!back && nav && hasNav && (
                            <div
                                role="group"
                                aria-label={nav.settled ? `${nav.group}, ${nav.total - 1} left` : `${nav.group}, ${nav.index + 1} of ${nav.total}`}
                                className="flex items-center gap-2"
                            >
                                <button
                                    type="button"
                                    disabled={!nav.prev || nav.disabled}
                                    onClick={() => move(nav.prev, "button")}
                                    aria-label={nav.prev ? `Previous: ${nav.prev.title}` : "Previous"}
                                    aria-keyshortcuts={nav.keys ? "K" : undefined}
                                    className={cn(ROUND, ROUND_TAP_ROW, ROUND_OFF, FOCUS_RING)}
                                >
                                    <ChevronUpIcon aria-hidden size={16} />
                                </button>
                                <button
                                    type="button"
                                    disabled={!nav.next || nav.disabled}
                                    onClick={() => move(nav.next, "button")}
                                    aria-label={nav.next ? `Next: ${nav.next.title}` : "Next"}
                                    aria-keyshortcuts={nav.keys ? "J" : undefined}
                                    className={cn(ROUND, ROUND_TAP_ROW, ROUND_OFF, FOCUS_RING)}
                                >
                                    <ChevronDownIcon aria-hidden size={16} />
                                </button>
                            </div>
                        )}
                        {closeButton(true)}
                    </div>
                </div>
            )}
            <div className={cn("flex items-start gap-3 border-b border-gray-100 px-6 pb-4 max-sm:px-4", rowA ? "pt-2" : "pt-5")}>
                {/* Empty until the sheet's <DrawerHead> portals into it. */}
                <div className="min-w-0 flex-1">{title !== undefined ? oldTitle : <div ref={setHead} />}</div>
                {!rowA && closeButton(false)}
            </div>
        </>
    );

    return createPortal(
        <DrawerSlotContext.Provider value={slots}>
            <div className="fixed inset-0 z-40">
                <div aria-hidden onClick={onClose} className="absolute inset-0 bg-black/25 motion-safe:animate-in motion-safe:fade-in" />
                <aside
                    ref={sheetRef}
                    role="dialog"
                    aria-modal="true"
                    aria-labelledby={titleId}
                    aria-describedby={describedBy}
                    onKeyDown={onKeyDown}
                    style={{ width, ...keyboardStyle }}
                    className="absolute right-0 top-0 flex h-full max-w-full flex-col bg-white shadow-2xl motion-safe:animate-in motion-safe:slide-in-from-right-8 motion-safe:duration-200"
                >
                    {header}
                    {/* White in the merchant shell, like its pages (redesign); the
                        blocks inside keep their hairline ring. Staff screens keep the grey. */}
                    <div ref={bodyRef} className={cn("flex-1 overflow-y-auto px-6 py-5", modern && "max-sm:px-4", whiteBody ? "bg-white" : PAGE_GROUND)}>
                        <div className="space-y-3">{children}</div>
                    </div>
                    {foot ? (
                        // Right-aligned, so the primary sits at the right edge; a
                        // note in the foot keeps its place on the left with `mr-auto`.
                        <div className="flex flex-wrap items-center justify-end gap-2 border-t border-gray-100 px-6 py-4">{foot}</div>
                    ) : (
                        // Empty, and hidden, until the sheet's <DrawerFoot> portals into it.
                        <div
                            ref={setFoot}
                            className="flex flex-col gap-2 border-t border-gray-100 px-6 pb-[max(12px,env(safe-area-inset-bottom))] pt-3 empty:hidden max-sm:px-4"
                        />
                    )}
                    <div ref={liveRef} aria-live="polite" aria-atomic="true" className="sr-only" />
                </aside>
            </div>
        </DrawerSlotContext.Provider>,
        document.body,
    );
}

/* ------------------------------------------------------------------------- *
 * Header
 * ------------------------------------------------------------------------- */

export interface DrawerHeadProps {
    /** An IconSquare (leadKind "square") for a file, or a 14px area icon ("icon") for a request, call or pack. None for a question. */
    lead?: ReactNode;
    leadKind?: "square" | "icon";
    /** Wraps in full. A question drawer's title is the question. */
    title: ReactNode;
    status?: DrawerStatus | null;
    /** Identity only (codes, kind, reference, paperclip count). Never flags. */
    chips?: ReactNode;
    /** Cut at 2 lines, the full text on hover: a doc title. A string title only carries the tooltip. */
    clampTitle?: boolean;
}

/** The sheet's header, portalled into the shell. Needs a <Drawer> without `title`. */
export function DrawerHead({ lead, leadKind = "icon", title, status, chips, clampTitle }: DrawerHeadProps) {
    const slots = useContext(DrawerSlotContext);
    if (!slots?.head) return null;
    const clock = status?.clock;
    const square = Boolean(lead) && leadKind === "square";
    return createPortal(
        <>
            <div className="flex items-start gap-3">
                {lead && <span className={cn("flex shrink-0 items-center justify-center", square ? "h-8" : "h-6 w-3.5")}>{lead}</span>}
                <h2
                    ref={slots.setTitleEl}
                    id={slots.titleId}
                    tabIndex={-1}
                    title={clampTitle && typeof title === "string" ? title : undefined}
                    className={cn(TYPE.tt, "m-0 min-w-0 flex-1 break-words text-gray-900 outline-none", clampTitle && "line-clamp-2", square && "pt-1")}
                >
                    {title}
                </h2>
            </div>
            {chips && <div className="mt-2 flex flex-wrap items-center gap-1.5">{chips}</div>}
            {status && (
                <p ref={slots.setStatusEl} id={slots.statusId} className={cn(TYPE.tc, "m-0 mt-1 text-gray-600")}>
                    {status.tone && <Dot tone={status.tone} className="mr-1.5 align-middle" />}
                    {status.label}
                    {clock?.text && (
                        <>
                            {" · "}
                            <span title={clock.title} className={cn("tabular-nums", clock.late && LATE_TEXT)}>
                                {clock.text}
                            </span>
                        </>
                    )}
                </p>
            )}
        </>,
        slots.head,
    );
}

/* ------------------------------------------------------------------------- *
 * Foot
 * ------------------------------------------------------------------------- */

export interface DrawerFootMessage {
    tone: "success" | "error" | "info";
    text: ReactNode;
}

/** Asks before a step that cannot be taken back (Change my answer, Close a call request, ...). */
export interface DrawerConfirm {
    text: ReactNode;
    verb: string;
    busy?: boolean;
    onConfirm(): void;
    onCancel(): void;
}

/** After a completing action: the item has left its group on the next read. */
export interface DrawerDone {
    /** What was done, e.g. "Accepted their wording". A trailing full stop is added if missing. */
    said: string;
    /** Items still to do: staff, live snapshot group minus this one; merchant, live queue minus this one. */
    left: number;
    /** The group word for staff: "{left} left in {group}." */
    group: string;
    next: DrawerSeqItem | null;
    audience: "staff" | "merchant";
    onNext(): void;
    /** Closes the drawer. */
    onDone(): void;
    /** Replaces "Nothing else needs us." (or the merchant's line) when that would be wrong, e.g. a Not sent yet group. */
    lastLine?: string;
    /** Merchant: the titles of what is left, named after the count ("2 more need you: Opening hours and Prices."). */
    leftNames?: string[];
}

export interface DrawerFootProps {
    message?: DrawerFootMessage | null;
    /** One PillButton or PillLink; variant="primary" only when it is the viewer's move. */
    first?: ReactNode;
    /** One PillButton, variant="secondary". */
    second?: ReactNode;
    confirm?: DrawerConfirm | null;
    done?: DrawerDone | null;
}

function doneLine(d: DrawerDone): string {
    const said = `${d.said.trim().replace(/[.!]+$/, "")}.`;
    if (d.left > 0) {
        if (d.audience === "merchant") {
            const names = (d.leftNames ?? []).filter(Boolean);
            const which = names.length > 0 && names.length <= 3 ? `: ${names.length === 1 ? names[0] : `${names.slice(0, -1).join(", ")} and ${names[names.length - 1]}`}` : "";
            return `${said} ${d.left} more ${d.left === 1 ? "needs" : "need"} you${which}.`;
        }
        return `${said} ${d.left} left in ${d.group}.`;
    }
    return `${said} ${d.lastLine ?? (d.audience === "merchant" ? "Nothing else needs you. Thank you." : "Nothing else needs us.")}`;
}

/**
 * Up to two pills; on a phone the first fills the row and both are 44px tall.
 * From 640px they sit at the right edge with the primary (`first`) last, on
 * the right, and the secondary to its left. Reversed in layout only, so the
 * primary is still first for a keyboard.
 */
function FootRow({ first, second }: { first?: ReactNode; second?: ReactNode }) {
    return (
        <div className="flex items-center gap-2 sm:flex-row-reverse">
            {first && (
                <div data-foot-slot="first" className="flex min-w-0 max-sm:flex-1 max-sm:*:h-11 max-sm:*:w-full">
                    {first}
                </div>
            )}
            {second && (
                <div data-foot-slot="second" className="flex min-w-0 max-sm:*:h-11">
                    {second}
                </div>
            )}
        </div>
    );
}

/** The sheet's foot, portalled into the shell. Needs a <Drawer> without `foot`. */
export function DrawerFoot({ message, first, second, confirm, done }: DrawerFootProps) {
    const slots = useContext(DrawerSlotContext);
    const rootRef = useRef<HTMLDivElement>(null);
    const saidRef = useRef<HTMLParagraphElement>(null);
    const confirmTextId = useId();
    const lastSaid = useRef<string | null>(null);
    /** The slot ("first", "second") last clicked, read when a confirm opens. */
    const lastSlot = useRef<string | null>(null);
    const confirmOpen = Boolean(confirm);
    const nextItem = done && done.left > 0 ? done.next : null;
    const doneKey = done ? `${done.said}|${done.left}|${nextItem?.id ?? ""}` : null;
    const announce = slots?.announce;

    // Say a success or info line, or the done line, once each time its words
    // change, through the shell's live region: a role=status node mounted
    // together with its words is not reliably read.
    useLayoutEffect(() => {
        const text = saidRef.current?.textContent?.replace(/\s+/g, " ").trim() || null;
        if (text && text !== lastSaid.current) announce?.(text);
        lastSaid.current = text;
    });

    // The confirm row: Cancel takes focus. When the row goes, focus goes
    // back to what opened it, unless something else has taken it since.
    useLayoutEffect(() => {
        if (!confirmOpen) return;
        const active = document.activeElement;
        const back = { slot: lastSlot.current, el: active instanceof HTMLElement && active !== document.body ? active : null };
        // The same node for as long as the foot shows anything: the pills come back inside it.
        const root = rootRef.current;
        root?.querySelector<HTMLElement>("[data-drawer-cancel]")?.focus();
        return () => {
            requestAnimationFrame(() => {
                const now = document.activeElement;
                if (now && now !== document.body && now.isConnected) return;
                if (back.el?.isConnected) {
                    back.el.focus();
                    return;
                }
                if (!root?.isConnected) return;
                const target =
                    root?.querySelector<HTMLElement>(`[data-foot-slot="${back.slot ?? "first"}"] :is(button, a)`) ?? root?.querySelector<HTMLElement>("button, a");
                target?.focus();
            });
        };
    }, [confirmOpen]);

    // Next (or Done) takes focus, so Enter moves on.
    useLayoutEffect(() => {
        if (doneKey === null) return;
        rootRef.current?.querySelector<HTMLElement>("[data-drawer-done]")?.focus();
    }, [doneKey]);

    // A click that opened nothing leaves no stale slot for the next confirm.
    useLayoutEffect(() => {
        lastSlot.current = null;
    });

    // The main button changes in place (Send, then Share, then Write the
    // email): for a moment after it changes it takes no click, so a
    // double-click never lands on the next step.
    const lastFirst = useRef<string | null>(null);
    const coolUntil = useRef(0);
    useLayoutEffect(() => {
        const text = rootRef.current?.querySelector('[data-foot-slot="first"]')?.textContent?.trim() || null;
        if (lastFirst.current !== null && text !== null && text !== lastFirst.current) coolUntil.current = Date.now() + 700;
        lastFirst.current = text;
    });

    useEscapeLayer(() => {
        if (confirm && !confirm.busy) confirm.onCancel();
    }, confirmOpen);

    const hasButtons = Boolean(first) || Boolean(second);
    if (!slots?.foot) return null;
    if (!done && !confirm && !hasButtons && !message) return null;

    let line: ReactNode = null;
    if (done) {
        line = (
            <p ref={saidRef} className={cn(TYPE.tc, "m-0 flex items-start gap-1.5 text-gray-700")}>
                <CheckIcon aria-hidden size={14} className="mt-0.5 shrink-0 text-emerald-600" />
                <span className="min-w-0 break-words">{doneLine(done)}</span>
            </p>
        );
    } else if (message?.tone === "error") {
        line = (
            <p role="alert" className={cn(TYPE.tc, LATE_TEXT, "m-0 break-words")}>
                {message.text}
            </p>
        );
    } else if (message) {
        line = (
            <p ref={saidRef} className={cn(TYPE.tc, "m-0 flex items-start gap-1.5", message.tone === "success" ? "text-gray-700" : "text-gray-600")}>
                {message.tone === "success" && <CheckIcon aria-hidden size={14} className="mt-0.5 shrink-0 text-emerald-600" />}
                <span className="min-w-0 break-words">{message.text}</span>
            </p>
        );
    }

    let row: ReactNode = null;
    if (done) {
        const onDoneKey = (e: KeyboardEvent<HTMLButtonElement>) => {
            if (!slots.navKeys || !nextItem) return;
            if (e.key.toLowerCase() !== "j" || e.altKey || e.ctrlKey || e.metaKey || e.shiftKey) return;
            e.preventDefault();
            e.stopPropagation();
            done.onNext();
        };
        row = (
            <FootRow
                first={(
                    <PillButton
                        variant="primary"
                        data-drawer-done=""
                        onClick={nextItem ? done.onNext : done.onDone}
                        onKeyDown={onDoneKey}
                        aria-label={nextItem ? `Next: ${nextItem.title}` : undefined}
                        aria-keyshortcuts={nextItem && slots.navKeys ? "J" : undefined}
                    >
                        {nextItem ? `Next: ${cut(nextItem.title, 40)}` : "Done"}
                    </PillButton>
                )}
            />
        );
    } else if (confirm) {
        row = (
            <>
                <p id={confirmTextId} className={cn(TYPE.tc, "m-0 break-words text-gray-900")}>
                    {confirm.text}
                </p>
                <FootRow
                    first={(
                        <PillButton variant="primary" writes busy={confirm.busy} aria-describedby={confirmTextId} onClick={confirm.onConfirm}>
                            {confirm.verb}
                        </PillButton>
                    )}
                    second={(
                        <PillButton data-drawer-cancel="" disabled={confirm.busy} onClick={confirm.onCancel}>
                            Cancel
                        </PillButton>
                    )}
                />
            </>
        );
    } else if (hasButtons) {
        row = <FootRow first={first} second={second} />;
    }

    const onClickCapture = (e: MouseEvent<HTMLDivElement>) => {
        const slot = e.target instanceof Element ? e.target.closest("[data-foot-slot]") : null;
        if (slot && Date.now() < coolUntil.current) {
            e.preventDefault();
            e.stopPropagation();
            return;
        }
        lastSlot.current = slot?.getAttribute("data-foot-slot") ?? null;
    };

    return createPortal(
        // `contents`: the line and the row are the foot's own flex items.
        <div ref={rootRef} className="contents" onClickCapture={onClickCapture}>
            {line}
            {row}
        </div>,
        slots.foot,
    );
}

/* ------------------------------------------------------------------------- *
 * Body
 * ------------------------------------------------------------------------- */

/** A white block in the drawer body, with an optional label above its content. `busy` marks it while it is written. */
export function DrawerBlock({ label, children, className, busy, id }: { label?: ReactNode; children: ReactNode; className?: string; busy?: boolean; id?: string }) {
    return (
        <div id={id} aria-busy={busy || undefined} className={cn(SURFACE.block, className)}>
            {label && <p className={cn(TYPE.lab, "m-0")}>{label}</p>}
            <div className={cn(TYPE.tb, "text-gray-900", Boolean(label) && "mt-1")}>{children}</div>
        </div>
    );
}

/**
 * Detail longer than one line, folded by default, with one fact on the head
 * while folded. Ids are namespaced per sheet ("staff-doc.file",
 * "merchant-question.help"). `remember` keeps a hand fold under
 * oura.fold.drawer.<id>, for staff; merchant folds start folded every time,
 * so a busy thread never follows the merchant from question to question.
 */
export function DrawerFold({
    id,
    label,
    note,
    remember = false,
    defaultOpen = false,
    keepMounted = false,
    children,
}: {
    id: string;
    label: ReactNode;
    /** Shown on the head only while folded. */
    note?: ReactNode;
    remember?: boolean;
    /** Start open (a thread with an unsent reply). A remembered state wins. */
    defaultOpen?: boolean;
    /** Keep the content mounted while folded, so a half-typed reply inside survives a fold. */
    keepMounted?: boolean;
    children: ReactNode;
}) {
    const [folded, setFolded] = useFoldState(remember ? foldKey("drawer", id) : null, !defaultOpen);
    const bodyId = useId();
    const showNote = folded && Boolean(note);
    return (
        <div className={cn(SURFACE.block, "py-0")}>
            <h3 className="m-0">
                <button
                    type="button"
                    aria-expanded={!folded}
                    aria-controls={bodyId}
                    onClick={() => setFolded(!folded)}
                    className={cn("flex min-h-11 w-full items-center gap-3 rounded-md text-left", FOCUS_RING)}
                >
                    <span className={cn(TYPE.lab, "shrink-0")}>{label}</span>
                    {showNote && <span className={cn(TYPE.tc, "ml-auto min-w-0 truncate text-gray-500")}>{note}</span>}
                    <ChevronDownIcon
                        aria-hidden
                        size={14}
                        className={cn(
                            "shrink-0 text-gray-400 transition-transform duration-200 motion-reduce:transition-none",
                            !folded && "rotate-180",
                            !showNote && "ml-auto",
                        )}
                    />
                </button>
            </h3>
            <div id={bodyId} hidden={folded} className={cn(TYPE.tb, "pb-4 text-gray-900")}>
                {(!folded || keepMounted) && children}
            </div>
        </div>
    );
}

/**
 * The other side's words: "who · where · date" over the text, cut at 6 lines
 * with "Show all" when it runs longer. `when` replaces the date when the
 * caller already has the words ("2 hours ago").
 */
export function DrawerQuote({
    who,
    where,
    at,
    when,
    children,
}: {
    who: string;
    where?: string | null;
    at?: string | null;
    when?: string | null;
    children: ReactNode;
}) {
    const textRef = useRef<HTMLDivElement>(null);
    const [all, setAll] = useState(false);
    const [over, setOver] = useState(false);
    useLayoutEffect(() => {
        const el = textRef.current;
        if (!el || all) return;
        const measure = () => setOver(el.scrollHeight > el.clientHeight + 1);
        measure();
        if (typeof ResizeObserver === "undefined") return;
        const ro = new ResizeObserver(measure);
        ro.observe(el);
        return () => ro.disconnect();
    }, [all, children]);
    const meta = joinDotted(who, where, when ?? dayDate(at));
    return (
        <div className={SURFACE.inner}>
            {meta && <p className={cn(TYPE.tc, "m-0 text-gray-500")}>{meta}</p>}
            <div ref={textRef} className={cn(TYPE.tb, "whitespace-pre-line break-words text-gray-900", Boolean(meta) && "mt-0.5", !all && "line-clamp-6")}>
                {children}
            </div>
            {(over || all) && (
                <button
                    type="button"
                    aria-expanded={all}
                    onClick={() => setAll(!all)}
                    className={cn(
                        TYPE.tc,
                        "relative mt-1 rounded font-medium text-blue-600 hover:text-blue-700 after:absolute after:-inset-x-1 after:-inset-y-[13px] after:content-['']",
                        FOCUS_RING,
                    )}
                >
                    {all ? "Show less" : "Show all"}
                </button>
            )}
        </div>
    );
}

/** The amber notice at the top of the body, for a failed or stale read. `action` is a text button ("Read the folder again"). */
export function DrawerNotice({ children, action }: { children: ReactNode; action?: ReactNode }) {
    return (
        <div className="flex flex-wrap items-start gap-x-3 gap-y-2 rounded-2xl bg-amber-50 p-4 ring-1 ring-inset ring-amber-200">
            <TriangleAlertIcon aria-hidden size={16} className="mt-0.5 shrink-0 text-amber-600" />
            <div className={cn(TYPE.tb, "min-w-0 flex-1 text-amber-900")}>{children}</div>
            {action && <div className="shrink-0">{action}</div>}
        </div>
    );
}

const PULSE_W = ["w-2/3", "w-1/2", "w-3/4", "w-2/5"] as const;

/** Loading bars for a block whose content has not come back yet, with an sr-only label. */
export function DrawerPulse({ lines = 2, label = "Loading…" }: { lines?: number; label?: string }) {
    return (
        <>
            <span className="sr-only">{label}</span>
            {Array.from({ length: lines }, (_, i) => (
                <span
                    key={i}
                    aria-hidden
                    className={cn("block h-3 animate-pulse rounded bg-gray-100 motion-reduce:animate-none", PULSE_W[i % PULSE_W.length], i > 0 && "mt-2")}
                />
            ))}
        </>
    );
}
